import type { TwilioMessage } from '@twilio/conversations';
import BaseChatItem from 'app/modules/virtual-assistant/components/chat-items/BaseChatItem';
import { linkify } from 'app/utils/linkify';
import useHTMLLinkClickHandler from 'app/hooks/useHTMLLinkClickHandler';
import { MessageType } from 'app/services/twilio/messages';

import JoiningMessage from './JoiningMessage';
import MessageBubble from './MessageBubble';
import { useConcierge } from '../hooks/useConcierge';
import DisclaimerMessage from './DisclaimerMessage';
import ConversationArchivedMessage from './ConversationArchivedMessage';

type Props = {
  hideAvatar?: boolean;
  isAuthor: boolean;
  message: TwilioMessage;
  prevMessageAuthor?: string | null;
};

const convertMarkdownLinks = (text: string) => {
  return text.replace(/\[([^\]]+)\]\(([^)]+)\)/gm, '<a href="$2">$1</a>');
};

const TwilioMessageFactory = ({ isAuthor, message, hideAvatar, prevMessageAuthor }: Props) => {
  const handleHTMLLinkClick = useHTMLLinkClickHandler();
  const avatarURL = useConcierge()?.avatar;
  const concierge = message.attributes?.concierge;
  const concierge_avatar = message.attributes?.concierge_avatar;
  const htmlString = linkify(convertMarkdownLinks(message.body || ''));

  const getSpacing = () => {
    if (prevMessageAuthor === message.author) {
      return 'mt-6 mb-0';
    } else {
      return 'mt-8 mb-0';
    }
  };

  // NOTE: this type of message is filtered, we don't render it here
  // if (message.attributes?.type === MessageType.QueTimeStarted) {
  //   return (
  //     <ConnectingMessage index={message.index} className="my-8 scroll-my-8" />
  //   );
  // }

  if (message.attributes?.type === MessageType.NNJoined) {
    return (
      <JoiningMessage
        messageIndex={message.index}
        name={concierge?.name || ''}
        src={concierge?.avatar}
        className="my-8 scroll-my-8"
      />
    );
  }

  if (message.attributes?.type === MessageType.Disclaimer) {
    return (
      <BaseChatItem
        messageIndex={message.index}
        showAvatar={false}
        isUserMessage={isAuthor}
      >
        <DisclaimerMessage
          onClick={handleHTMLLinkClick}
          message={linkify(message.body ? message.body : message.attributes?.message)}
        />
      </BaseChatItem>
    );
  }

  if (message.attributes?.type === MessageType.ChatArchived) {
    return <ConversationArchivedMessage index={message.index} />;
  }

  const component = (
    <BaseChatItem
      messageIndex={message.index}
      showAvatar={!hideAvatar}
      avatarURL={concierge_avatar || avatarURL}
      isUserMessage={isAuthor}
      className={getSpacing()}
    >
      <MessageBubble
        isAuthor={isAuthor}
        onClick={handleHTMLLinkClick}
        dangerouslySetInnerHTML={{ __html: htmlString }}
      />
    </BaseChatItem>
  );

  return component;
};

export default TwilioMessageFactory;
